import { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { storeConfig } from '../../../../store.config'
import { Alert } from '../../components/atoms/Alert'
import { Spinner } from '../../components/atoms/Spinner'
import { Paginator } from '../../components/molecules/Paginator'
import { StorefrontBackdrop, StorefrontNav } from '../../components/storefront/chrome'
import { Reveal, Stagger, StaggerItem } from '../../motion'
import { useProfileContext } from '../../../context/ProfileContext'
import {
  useShopProducts,
  type ShopProductsFilter
} from '../../../viewmodels/useShopProducts'
import { ShopProductCard } from './ShopProductCard'
import { ShopFilterDrawer } from './filters/ShopFilterDrawer'

/**
 * `/shop` — PUBLIC product list. Sidebar filters (price + category +
 * conditional CDT panels) + search-in-nav + filter toggle. Cards click
 * through to `/shop/:id` (ShopProductPage detail).
 *
 * Filter state lives in URL search params so the list is shareable,
 * back/forward-friendly, and survives refresh.
 */
export function ShopListPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const profile = useProfileContext()
  // Filter panel starts CLOSED — the customer reveals it via the
  // funnel icon in the nav (or the mobile "Filters" button); click
  // again to collapse. Per Kiran 2026-06-05.
  const [filterOpen, setFilterOpen] = useState(false)

  // Parse the canonical filter from URL on every render. Cheap; the
  // values are referentially unstable but that's fine — useShopProducts
  // resets to page 1 when the filter object changes.
  const filter: ShopProductsFilter = useMemo(() => {
    const q = searchParams.get('q') ?? ''
    const categoryIds = (searchParams.get('categoryIds') ?? '')
      .split(',').map((s) => s.trim()).filter((s) => s.length > 0)
    const min = searchParams.get('priceMinPaisa')
    const max = searchParams.get('priceMaxPaisa')
    const attributeValues: Record<string, ReadonlyArray<string>> = {}
    for (const [k, v] of searchParams.entries()) {
      if (!k.startsWith('attr_')) continue
      const attrId = k.slice('attr_'.length)
      const vals = v.split(',').map((s) => s.trim()).filter((s) => s.length > 0)
      if (vals.length > 0) attributeValues[attrId] = vals
    }
    return {
      q,
      categoryIds,
      priceMinPaisa: min === null || min === '' ? null : Number(min),
      priceMaxPaisa: max === null || max === '' ? null : Number(max),
      attributeValues
    }
  }, [searchParams])

  const writeFilter = useCallback(
    (next: ShopProductsFilter): void => {
      const params = new URLSearchParams()
      if (next.q.trim().length > 0) params.set('q', next.q.trim())
      if (next.categoryIds.length > 0) params.set('categoryIds', next.categoryIds.join(','))
      if (next.priceMinPaisa !== null) params.set('priceMinPaisa', String(next.priceMinPaisa))
      if (next.priceMaxPaisa !== null) params.set('priceMaxPaisa', String(next.priceMaxPaisa))
      for (const [attrId, vals] of Object.entries(next.attributeValues)) {
        if (vals.length === 0) continue
        params.set(`attr_${attrId}`, vals.join(','))
      }
      setSearchParams(params, { replace: true })
    },
    [setSearchParams]
  )

  // Debounce the search input so we don't refetch on every keystroke.
  const [draftQ, setDraftQ] = useState<string>(filter.q)
  useEffect(() => { setDraftQ(filter.q) }, [filter.q])
  useEffect(() => {
    if (draftQ === filter.q) return
    const id = window.setTimeout(() => writeFilter({ ...filter, q: draftQ }), 300)
    return () => window.clearTimeout(id)
  }, [draftQ, filter, writeFilter])

  const vm = useShopProducts(filter)

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      <Helmet>
        <title>{`Shop · ${storeConfig.brand.name}`}</title>
      </Helmet>
      <div className="fixed inset-0 z-0">
        <StorefrontBackdrop />
      </div>
      <StorefrontNav
        onProfile={() => profile.open()}
        searchQuery={draftQ}
        onSearchChange={setDraftQ}
        onFilterToggle={() => setFilterOpen((v) => !v)}
        fullWidth
        showCart
        showBuild
      />
      {/* Edge-to-edge with dashboard-style horizontal padding (px-6 sm:px-8)
          so the tile row hugs the left like every other operator screen.
          Per Kiran 2026-06-05. */}
      <main className="relative z-10 px-6 pb-16 pt-28 sm:px-8 sm:pt-32">
        {/* Grid spans the full width — the filter panel is now a
            slide-OVER drawer (Kiran 2026-06-05), not an inline column,
            so the layout doesn't reflow on open/close. */}
        <section className="space-y-6">
          {vm.error !== null ? (
            <Alert tone="error">
              {vm.error}{' '}
              <button
                type="button"
                className="underline underline-offset-2 hover:text-charcoal"
                onClick={vm.reload}
              >
                Retry
              </button>
            </Alert>
          ) : null}

          {vm.isLoading && vm.items.length === 0 ? (
            <div className="flex items-center justify-center py-24">
              <Spinner />
            </div>
          ) : vm.items.length === 0 ? (
            <Placeholder>
              No products match your filters. Adjust the filters or clear the search.
            </Placeholder>
          ) : (
            <>
              {/* Tile width is fixed so the row packs left-to-right and
                  wraps naturally — no empty grid cells when the result
                  set is sparse (e.g. one product on a wide viewport).
                  Cinematic 2026-06-08: tiles cascade in via the shared
                  Stagger primitive (~0.08s step) the first time the list
                  scrolls into view. */}
              <Stagger as="ul" className="flex flex-wrap gap-4" amount={0.05}>
                {vm.items.map((p) => (
                  <StaggerItem as="li" key={p.id} className="w-full sm:w-64">
                    <ShopProductCard product={p} />
                  </StaggerItem>
                ))}
              </Stagger>
              <Reveal>
                <Paginator
                  page={vm.page}
                  pageSize={vm.pageSize}
                  total={vm.total}
                  onPageChange={vm.setPage}
                  disabled={vm.isLoading}
                />
              </Reveal>
            </>
          )}
        </section>
      </main>

      <AnimatePresence>
        {filterOpen ? (
          <ShopFilterDrawer
            filter={filter}
            onFiltersChange={writeFilter}
            onClose={() => setFilterOpen(false)}
          />
        ) : null}
      </AnimatePresence>
    </div>
  )
}

function Placeholder({ children }: { readonly children: React.ReactNode }): JSX.Element {
  return (
    <div className="rounded-2xl border border-border/40 bg-bg-primary/72 px-6 py-12 text-center shadow-sm backdrop-blur-md">
      <p className="font-body text-body text-text-secondary">{children}</p>
    </div>
  )
}
